import { useState, useCallback } from 'react';
import { Check, Droplets, FlaskConical } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { Burette } from '@/components/lab';

const stepInfoContent: Record<number, { title: string; content: string; formulas?: string[] }> = {
  1: {
    title: 'Rinsing the Burette',
    content: 'Rinsing with NaOH solution removes any water that would dilute the titrant. Use small portions (about 5 mL) and rotate the burette to coat all inner surfaces. Drain through the tip to rinse the stopcock area. Repeat 2-3 times for thorough conditioning.',
    formulas: ['Dilution error = (Vol water / Vol titrant) x 100%']
  },
  2: {
    title: 'Filling with Titrant',
    content: 'Fill the burette through the top using a funnel. The ~0.10 M NaOH solution was previously standardized in analytical techniques, meaning its exact concentration is known. Overfill slightly above the zero mark - excess will be drained during priming.',
    formulas: ['~0.10 M NaOH (previously standardized)', 'M = n/V']
  },
  3: {
    title: 'Priming the Stopcock',
    content: 'Air bubbles below the stopcock cause volume errors during titration. Open the stopcock briefly to flush out air and bring the meniscus near zero. Check for bubbles by looking at the tip against a white background.',
    formulas: ['Error from air bubble = (bubble volume / total volume) x 100%']
  },
  4: {
    title: 'Reading the Meniscus',
    content: 'Read the burette at eye level to avoid parallax error. The bottom of the meniscus should be read against a white background. Record to two decimal places (0.01 mL precision). A meniscus reader card can help visualize the curve.',
    formulas: ['Precision: +/- 0.01 mL', 'Initial reading is typically near 0.00-1.00 mL']
  }
};

export default function Part2BuretteSetup() {
  const [rinsed, setRinsed] = useState(false);
  const [filled, setFilled] = useState(false);
  const [primed, setPrimed] = useState(false);
  const [recorded, setRecorded] = useState(false);
  const [buretteVolume, setBuretteVolume] = useState(0);
  const [initialReading, setInitialReading] = useState(0);
  const [currentStep, setCurrentStep] = useState(1);
  const [showInfo, setShowInfo] = useState<number | null>(null);

  const completedSteps = [rinsed, filled, primed, recorded].filter(Boolean).length;

  const handleRinse = useCallback(() => {
    if (!rinsed) {
      setRinsed(true);
      setCurrentStep(2);
    }
  }, [rinsed]);

  const handleFill = useCallback(() => {
    if (rinsed && !filled) {
      setBuretteVolume(50);
      setFilled(true);
      setCurrentStep(3);
    }
  }, [rinsed, filled]);

  const handlePrime = useCallback(() => {
    if (filled && !primed) {
      setBuretteVolume(48);
      setPrimed(true);
      setCurrentStep(4);
    }
  }, [filled, primed]);

  const handleRecord = useCallback(() => {
    if (primed && !recorded) {
      const reading = 0.85 + Math.random() * 0.3;
      setInitialReading(parseFloat(reading.toFixed(2)));
      setRecorded(true);
    }
  }, [primed, recorded]);

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part II: Burette Preparation</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Rinse Burette', hint: 'Rinse with NaOH solution', done: rinsed },
            { id: 2, title: 'Fill Burette', hint: 'Fill with ~0.10 M NaOH (previously standardized)', done: filled },
            { id: 3, title: 'Prime Stopcock', hint: 'Remove air bubbles', done: primed },
            { id: 4, title: 'Record Initial', hint: 'Read the meniscus level', done: recorded }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <LabBench
        experimentName="Burette Preparation"
        completedCount={completedSteps}
        totalSteps={4}
        equipment={['50 mL Burette', 'Burette clamp', '~0.10 M NaOH (previously standardized)']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="burette-workspace">
          <div className="burette-area">
            <div
              className={`burette-container ${currentStep <= 3 && !primed ? 'clickable' : ''}`}
              onClick={() => {
                if (currentStep === 1) handleRinse();
                else if (currentStep === 2) handleFill();
                else if (currentStep === 3) handlePrime();
              }}
              style={{ cursor: currentStep <= 3 && !primed ? 'pointer' : 'default' }}
            >
              <Burette
                volume={buretteVolume}
                maxVolume={50}
                liquidColor="#E3F2FD"
                stopcock={primed ? 'closed' : 'open'}
              />
            </div>

            {/* NaOH Solution Bottle */}
            <div className="naoh-bottle">
              <svg viewBox="0 0 60 100" width={80} height={120}>
                <rect x="15" y="30" width="30" height="60" rx="3" fill="#5A5A5A" />
                <rect x="20" y="40" width="20" height="45" fill="#E3F2FD" opacity="0.8" />
                <rect x="18" y="15" width="24" height="15" rx="2" fill="#333" />
                <text x="30" y="70" fontSize="6" fill="#333" textAnchor="middle">NaOH</text>
                <text x="30" y="80" fontSize="5" fill="#333" textAnchor="middle">~0.10 M</text>
              </svg>
              <span className="bottle-label">~0.10 M NaOH</span>
            </div>
          </div>

          {/* Reading Display */}
          {recorded && (
            <div className="reading-display">
              <span className="reading-label">Initial Reading:</span>
              <span className="reading-value">{initialReading.toFixed(2)} mL</span>
            </div>
          )}

          {/* Status Messages */}
          <div className="status-area">
            {!rinsed && (
              <div className="status-message info">
                <Droplets size={20} />
                Click the burette to rinse with NaOH solution
              </div>
            )}
            {rinsed && !filled && (
              <div className="status-message info">
                <Droplets size={20} />
                Click to fill the burette with ~0.10 M NaOH
              </div>
            )}
            {filled && !primed && (
              <div className="status-message info">
                <Droplets size={20} />
                Click to prime the stopcock and remove air bubbles
              </div>
            )}
            {primed && !recorded && (
              <div className="status-message info" onClick={handleRecord} style={{ cursor: 'pointer' }}>
                <FlaskConical size={20} />
                Click here to record the initial burette reading
              </div>
            )}
            {recorded && (
              <div className="status-message success">
                <Check size={20} />
                Burette prepared! Initial reading: {initialReading.toFixed(2)} mL
              </div>
            )}
          </div>

          {/* Theory Box */}
          <div className="theory-box">
            <h4>Burette Technique</h4>
            <p>
              Always rinse the burette with the titrant solution to prevent dilution.
              Check for and remove air bubbles below the stopcock - they will cause
              reading errors. Read the meniscus at eye level, recording to 0.01 mL.
            </p>
          </div>
        </div>

        <style>{`
          .burette-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .burette-area {
            display: flex;
            gap: 60px;
            align-items: flex-end;
            justify-content: center;
          }

          .burette-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 10px;
            border-radius: 8px;
            transition: all 0.2s ease;
          }

          .burette-container.clickable {
            cursor: pointer;
          }

          .burette-container.clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          .naoh-bottle {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .bottle-label {
            font-size: 12px;
            color: var(--text-secondary);
          }

          .reading-display {
            background: var(--bg-elevated);
            padding: 12px 24px;
            border-radius: 8px;
            display: flex;
            gap: 12px;
            align-items: center;
          }

          .reading-label {
            font-size: 14px;
            color: var(--text-secondary);
          }

          .reading-value {
            font-size: 18px;
            font-weight: 600;
            font-family: 'Courier New', monospace;
            color: var(--secondary);
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .theory-box p {
            margin: 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>
      </LabBench>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp9"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
